import React, { useEffect, useRef, useCallback } from "react";

/** Particle-only circular Chladni-style animation (no text morphing) */

/** Canvas + simulation */
const CSS_SIZE = 180; // CSS pixels (canvas will be DPR-scaled)
const CIRCLE_RADIUS = CSS_SIZE / 2 - 1;

/** Particles */
const NUM_PARTICLES = 1500;
const PARTICLE_SIZE = 0.4; // px

/** Wave field */
const BASE_WAVE_FREQUENCY = 0.23; // Hz-ish (relative)
const WAVE_VELOCITY = 0.16; // px/frame
const WAVE_PUSH = -0.15; // gradient push strength

/** Dynamics */
const GLOBAL_FRICTION = 1.0;
const MAX_SPEED = 1.25; // px/frame
const MICRO_NOISE = 0.03; // tiny random walk to avoid stillness

class WaveSource {
  constructor(public x: number, public y: number) {}
}

class Particle {
  x: number;
  y: number;
  vx = 0;
  vy = 0;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  private wave(x: number, y: number, sources: WaveSource[], t: number) {
    let a = 0;
    for (let i = 0; i < sources.length; i++) {
      const s = sources[i];
      const d = Math.hypot(x - s.x, y - s.y);
      const f = BASE_WAVE_FREQUENCY + Math.sin(t * 0.005 + i) * 0.03;
      a += Math.sin((2 * Math.PI * f * (t - d / WAVE_VELOCITY)) / 60);
    }
    return a;
  }

  update(sources: WaveSource[], t: number, w: number, h: number) {
    // Wave-driven gradient (Chladni-ish): move down gradient of |wave|
    if (sources.length) {
      const wc = this.wave(this.x, this.y, sources, t);
      const wr = this.wave(this.x + 1, this.y, sources, t);
      const wd = this.wave(this.x, this.y + 1, sources, t);
      const gx = Math.abs(wr) - Math.abs(wc);
      const gy = Math.abs(wd) - Math.abs(wc);
      const gl = Math.hypot(gx, gy);
      if (gl > 0) {
        this.vx += (WAVE_PUSH * gx) / gl;
        this.vy += (WAVE_PUSH * gy) / gl;
      }
    }

    // Gentle stochastic drift
    this.vx += (Math.random() - 0.5) * MICRO_NOISE;
    this.vy += (Math.random() - 0.5) * MICRO_NOISE;

    // Friction + clamp
    this.vx *= GLOBAL_FRICTION;
    this.vy *= GLOBAL_FRICTION;
    const sp = Math.hypot(this.vx, this.vy);
    if (sp > MAX_SPEED) {
      const s = MAX_SPEED / sp;
      this.vx *= s;
      this.vy *= s;
    }

    // Integrate
    this.x += this.vx;
    this.y += this.vy;

    // Reflective circular boundary
    const cx = w / 2, cy = h / 2;
    const dx = this.x - cx, dy = this.y - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > CIRCLE_RADIUS - 0.5) {
      // Project back to rim
      const s = (CIRCLE_RADIUS - 0.5) / (dist || 1);
      const nx = cx + dx * s, ny = cy + dy * s;
      // Reflect velocity over outward normal (soft bounce)
      const nxv = (this.vx * dx + this.vy * dy) / (dist || 1);
      this.vx -= 1.6 * ((nxv * dx) / (dist || 1));
      this.vy -= 1.6 * ((nxv * dy) / (dist || 1));
      this.x = nx;
      this.y = ny;
    }
  }

  draw(ctx: CanvasRenderingContext2D, sources: WaveSource[], t: number) {
    // Alpha follows wave intensity for a shimmering look
    let alpha = 1;
    if (sources.length) {
      const waveAmp = this.wave(this.x, this.y, sources, t);
      const intensity = 1 - Math.abs(waveAmp); // 0..1
      alpha = 0.15 + 0.85 * intensity; // keep some visibility everywhere
    }

    ctx.save();
    ctx.globalAlpha = Math.max(0.05, Math.min(1, alpha));
    ctx.fillStyle = "white";
    ctx.beginPath();
    ctx.arc(this.x, this.y, PARTICLE_SIZE, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

const VoiceParticleAnimation: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const raf = useRef<number | null>(null);
  const particles = useRef<Particle[]>([]);
  const sources = useRef<WaveSource[]>([]);
  const time = useRef(0);

  /** HiDPI-safe canvas setup */
  const setupCanvas = (canvas: HTMLCanvasElement) => {
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = CSS_SIZE * dpr;
    canvas.height = CSS_SIZE * dpr;
    canvas.style.width = `${CSS_SIZE}px`;
    canvas.style.height = `${CSS_SIZE}px`;
    const ctx = canvas.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return ctx;
  };

  /** Init: random particles inside the circle + ringed wave sources */
  const init = useCallback((w: number, h: number) => {
    particles.current = [];
    sources.current = [];

    // Randomly distribute particles uniformly within the circle
    for (let i = 0; i < NUM_PARTICLES; i++) {
      const r = Math.sqrt(Math.random()) * (CIRCLE_RADIUS - 2);
      const a = Math.random() * Math.PI * 2;
      const x = w / 2 + r * Math.cos(a);
      const y = h / 2 + r * Math.sin(a);
      particles.current.push(new Particle(x, y));
    }

    // 4 wave sources on a ring (mildly offset from center)
    const N = 4;
    const ring = CIRCLE_RADIUS * 0.6;
    for (let i = 0; i < N; i++) {
      const ang = (i / N) * Math.PI * 2;
      sources.current.push(
        new WaveSource(w / 2 + Math.cos(ang) * ring, h / 2 + Math.sin(ang) * ring)
      );
    }

    time.current = 0;
  }, []);

  const tick = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const w = CSS_SIZE, h = CSS_SIZE;
    const cx = w / 2, cy = h / 2;

    ctx.clearRect(0, 0, w, h);

    // Clip to circle
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, CIRCLE_RADIUS, 0, Math.PI * 2);
    ctx.clip();

    // Update + draw
    for (const p of particles.current) p.update(sources.current, time.current, w, h);
    for (const p of particles.current) p.draw(ctx, sources.current, time.current);

    ctx.restore();

    // Outer glow ring
    ctx.save();
    const ex = Math.floor(cx) + 0.5, ey = Math.floor(cy) + 0.5;
    ctx.shadowColor = "rgba(255,255,255,0.8)";
    ctx.shadowBlur = 20;
    ctx.strokeStyle = "rgba(255,255,255,0.3)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.arc(ex, ey, CIRCLE_RADIUS, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();

    time.current += 1;
    raf.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setupCanvas(canvas);
    init(CSS_SIZE, CSS_SIZE);
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [init, tick]);

  return (
    <div className="flex items-center justify-center">
      <canvas
        ref={canvasRef}
        style={{
          width: `${CSS_SIZE}px`,
          height: `${CSS_SIZE}px`,
          background: "transparent",
          border: "none",
          borderRadius: "50%",
          boxShadow:
            "0 0 30px 10px rgba(255, 255, 255, 0.3), 0 0 60px 20px rgba(255, 255, 255, 0.1)",
        }}
      />
    </div>
  );
};

export default VoiceParticleAnimation;
